import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../material.module';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { DashboardTreeService, WellSearchResult, WellSearchParams } from '../../services/dashboard-tree.service';
import { CreateCustomWellDialogComponent } from '../create-custom-well-dialog/create-custom-well-dialog.component';

export interface WellSearchDialogData {
    businessUnits: { code: string; name: string }[];
}

export interface WellSearchDialogResult {
    wellId: number;
    wellName: string;
    businessUnitCode: string;
    businessUnitName: string;
    wellLocation?: string;
}

@Component({
    selector: 'app-well-search-dialog',
    standalone: true,
    imports: [MaterialModule, FormsModule, SpinnerComponent],
    templateUrl: './well-search-dialog.component.html',
    styleUrls: ['./well-search-dialog.component.css'],
})
export class WellSearchDialogComponent {
    searchForm = {
        wellName: '',
        api12: '',
        chevNo: '',
        fieldName: '',
        leaseName: '',
    };

    searchResults: WellSearchResult[] = [];
    isSearching = false;
    isCreatingWell = false;
    hasSearched = false;

    contextMenuPosition = { x: '0px', y: '0px' };
    selectedResultWell: WellSearchResult | null = null;

    @ViewChild('wellResultMenuTrigger') wellResultMenuTrigger!: MatMenuTrigger;

    get isSearchDisabled(): boolean {
        return !Object.values(this.searchForm).some(v => v.trim());
    }

    constructor(
        private dialogRef: MatDialogRef<WellSearchDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: WellSearchDialogData,
        private dashboardTreeService: DashboardTreeService,
        private dialog: MatDialog,
    ) { }

    searchWells(): void {
        const params: WellSearchParams = {};
        if (this.searchForm.wellName.trim()) params.WellName = this.searchForm.wellName.trim();
        if (this.searchForm.api12.trim()) params.Api12 = this.searchForm.api12.trim();
        if (this.searchForm.chevNo.trim()) params.ChevNo = this.searchForm.chevNo.trim();
        if (this.searchForm.fieldName.trim()) params.FieldName = this.searchForm.fieldName.trim();
        if (this.searchForm.leaseName.trim()) params.LeaseName = this.searchForm.leaseName.trim();

        this.isSearching = true;
        this.hasSearched = false;
        this.searchResults = [];

        this.dashboardTreeService.searchWells(params).subscribe({
            next: (results) => {
                this.searchResults = results;
                this.isSearching = false;
                this.hasSearched = true;
            },
            error: (err) => {
                console.error('Well search failed', err);
                this.isSearching = false;
                this.hasSearched = true;
            },
        });
    }

    onResultContextMenu(event: MouseEvent, well: WellSearchResult): void {
        event.preventDefault();
        event.stopPropagation();
        this.contextMenuPosition.x = event.clientX + 'px';
        this.contextMenuPosition.y = event.clientY + 'px';
        this.selectedResultWell = well;
        this.wellResultMenuTrigger.openMenu();
    }

    createProjectForWell(well?: WellSearchResult): void {
        if (!well) {
            well = this.selectedResultWell ?? undefined;
        }
        if (!well) return;
        const buName = this.data.businessUnits.find(b => b.code === well.sbuCode)?.name ?? well.sbuCode;

        if (well.cementWellId) {
            this.dialogRef.close({
                wellId: well.cementWellId,
                wellName: well.wellName,
                businessUnitCode: well.sbuCode,
                businessUnitName: buName,
            } as WellSearchDialogResult);
            return;
        }

        const customDialogRef = this.dialog.open(CreateCustomWellDialogComponent, {
            width: '600px',
            disableClose: true,
            data: {
                wellSearchResult: well,
            },
        });
        customDialogRef.afterClosed().subscribe((result: WellSearchDialogResult | null) => {
            if (result) {
                this.dialogRef.close(result);
            }
        });
    }

    createWithCustomWell(): void {
        const customDialogRef = this.dialog.open(CreateCustomWellDialogComponent, {
            width: '600px',
            disableClose: true,
        });
        customDialogRef.afterClosed().subscribe((result: WellSearchDialogResult | null) => {
            if (result) {
                this.dialogRef.close(result);
            }
        });
    }

    cancel(): void {
        this.dialogRef.close(null);
    }
}
